import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  HairlineCard,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  FormHelperText,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  CouncilDecision,
  FileDetail,
  InspectionDecision,
  Step7Data,
} from '@munifocus/contracts-gov';
import { EntryWarrantPanel } from '../../components/EntryWarrantPanel';
import { EscalationStepLayout } from '../../components/EscalationStepLayout';
import { useSaveStage } from '../../lib/files-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';

/**
 * Type 3 sub-flow, Step 7 — Council intervention (ticket E4). Council may
 * authorize one or more actions: refuse closes the file; work routes to Step 8;
 * proceedings routes to Step 9 (work + proceedings may both be authorized).
 * Refuse is mutually exclusive with the other two. Captures the optional
 * current-state inspection decision and a finance-consultation flag.
 */
export function Step7Council({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'step_7');
  const initial = (file.stageData?.step_7 ?? {}) as Step7Data;
  const seeded = initial.councilDecisions ?? [];

  const [inspectionDecision, setInspectionDecision] = useState<InspectionDecision | ''>(
    initial.inspectionDecision ?? '',
  );
  const [financeConsultation, setFinanceConsultation] = useState(
    initial.financeConsultation ?? false,
  );
  const [refuse, setRefuse] = useState(seeded.includes('refuse'));
  const [work, setWork] = useState(seeded.includes('work'));
  const [proceedings, setProceedings] = useState(seeded.includes('proceedings'));

  // Refuse is terminal and exclusive; selecting it clears the intervene paths,
  // and selecting an intervene path clears refuse.
  const chooseRefuse = (on: boolean) => {
    setRefuse(on);
    if (on) {
      setWork(false);
      setProceedings(false);
    }
  };
  const chooseWork = (on: boolean) => {
    setWork(on);
    if (on) setRefuse(false);
  };
  const chooseProceedings = (on: boolean) => {
    setProceedings(on);
    if (on) setRefuse(false);
  };

  const councilDecisions: CouncilDecision[] = [
    ...(refuse ? (['refuse'] as const) : []),
    ...(work ? (['work'] as const) : []),
    ...(proceedings ? (['proceedings'] as const) : []),
  ];
  const v = useSubmitValidation({
    councilDecisions:
      councilDecisions.length > 0 ? null : t('gov.escalation.step7.decisionRequired'),
  });

  const onSave = () =>
    save.mutate(
      {
        inspectionDecision: inspectionDecision || undefined,
        financeConsultation,
        councilDecisions,
        // Refuse is terminal — advance escalation -> closed.
        advance: refuse,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  return (
    <EscalationStepLayout
      file={file}
      step="step_7"
      onSave={v.guard(onSave)}
      saving={save.isPending}
      saveLabel={refuse ? t('gov.escalation.step6.closeFile') : t('common.actions.save')}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.escalation.step7.inspection')}</InputLabel>
        <Select
          label={t('gov.escalation.step7.inspection')}
          aria-label={t('gov.escalation.step7.inspection')}
          value={inspectionDecision}
          onChange={(e) => setInspectionDecision(e.target.value as InspectionDecision)}
        >
          <MenuItem value="reuse">{t('gov.escalation.step7.inspectionReuse')}</MenuItem>
          <MenuItem value="refresh">{t('gov.escalation.step7.inspectionRefresh')}</MenuItem>
        </Select>
      </FormControl>
      {inspectionDecision === 'refresh' && (
        <>
          <Alert severity="info">{t('gov.escalation.step7.warrantNote')}</Alert>
          {/* Step 7.1 — a fresh inspection may need an entry warrant (brief §6). */}
          <EntryWarrantPanel fileId={file.id} triggerPhase="step-7" />
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={financeConsultation}
            onChange={(e) => setFinanceConsultation(e.target.checked)}
          />
        }
        label={t('gov.escalation.step7.finance')}
      />

      <Divider />

      <Typography variant="subtitle2">{t('gov.escalation.step7.decision')}</Typography>
      <HairlineCard sx={{ p: 2 }}>
        <FormControl component="fieldset" error={Boolean(v.errors.councilDecisions)}>
          <FormControlLabel
            control={
              <Checkbox
                checked={work}
                slotProps={{ input: { ref: v.register('councilDecisions') } }}
                onChange={(e) => chooseWork(e.target.checked)}
              />
            }
            label={t('gov.escalation.step7.work')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={proceedings}
                onChange={(e) => chooseProceedings(e.target.checked)}
              />
            }
            label={t('gov.escalation.step7.proceedings')}
          />
          <FormControlLabel
            control={<Checkbox checked={refuse} onChange={(e) => chooseRefuse(e.target.checked)} />}
            label={t('gov.escalation.step7.refuse')}
          />
          {v.errors.councilDecisions && (
            <FormHelperText>{v.errors.councilDecisions}</FormHelperText>
          )}
        </FormControl>
      </HairlineCard>

      {refuse && <Alert severity="warning">{t('gov.escalation.step7.refuseNote')}</Alert>}
      {(work || proceedings) && (
        <Alert severity="info">
          {t('gov.escalation.step7.routeNote', {
            steps: [
              work ? t('gov.escalation.steps.step_8') : null,
              proceedings ? t('gov.escalation.steps.step_9') : null,
            ]
              .filter(Boolean)
              .join(' · '),
          })}
        </Alert>
      )}
    </EscalationStepLayout>
  );
}
